import { useCallback, useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { cropImageToFile } from "helpers/cropImageFile";

type ProdutoImageCropModalProps = {
  open: boolean;
  file: File | null;
  maxWidth: number;
  maxHeight: number;
  title?: string;
  previewLabel?: string;
  onClose: () => void;
  onConfirm: (file: File) => void;
  onError?: (message: string) => void;
};

const PREVIEW_MAX_WIDTH = 160;

export default function ProdutoImageCropModal({
  open,
  file,
  maxWidth,
  maxHeight,
  title = "Ajustar imagem do produto",
  previewLabel = "Prévia",
  onClose,
  onConfirm,
  onError,
}: ProdutoImageCropModalProps) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [sourceSize, setSourceSize] = useState({ width: 0, height: 0 });

  const aspect = maxWidth / maxHeight;
  const previewWidth = PREVIEW_MAX_WIDTH;
  const previewHeight = Math.round(PREVIEW_MAX_WIDTH / aspect);

  useEffect(() => {
    if (!open || !file) {
      setImageSrc("");
      return;
    }

    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);

    const img = new Image();
    img.onload = () => {
      setSourceSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [open, file]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!file || !imageSrc) return;

    if (!croppedAreaPixels) {
      onError?.("Aguarde o carregamento do recorte ou mova a imagem levemente.");
      return;
    }

    setSaving(true);
    try {
      const mimeType =
        file.type === "image/png" || file.type === "image/webp"
          ? file.type
          : "image/jpeg";
      const ext =
        mimeType === "image/png"
          ? ".png"
          : mimeType === "image/webp"
            ? ".webp"
            : ".jpg";
      const baseName = file.name.replace(/\.[^.]+$/, "") || "imagem";

      const cropped = await cropImageToFile(
        imageSrc,
        croppedAreaPixels,
        `${baseName}${ext}`,
        maxWidth,
        maxHeight,
        mimeType,
      );
      onConfirm(cropped);
    } catch (error: any) {
      onError?.(error?.message || "Falha ao gerar a imagem recortada.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          Área permitida: até {maxWidth} × {maxHeight} px
          {sourceSize.width > 0 &&
            ` · Original: ${sourceSize.width} × ${sourceSize.height} px`}
        </MDTypography>

        <MDBox
          sx={{
            position: "relative",
            width: "100%",
            height: 320,
            bgcolor: "#212529",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </MDBox>

        <MDBox mt={2} px={1}>
          <MDTypography variant="caption" color="text">
            Zoom
          </MDTypography>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{
              width: "100%",
              marginTop: 8,
              accentColor: "#344767",
            }}
          />
        </MDBox>

        <MDBox
          mt={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: previewWidth,
            height: previewHeight,
            mx: "auto",
            border: "2px dashed",
            borderColor: "#7b809a",
            borderRadius: 1,
            bgcolor: "#f8f9fa",
            position: "relative",
          }}
        >
          <MDTypography variant="caption" color="text" textAlign="center">
            {previewLabel}
            <br />
            {maxWidth} × {maxHeight} px
          </MDTypography>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleConfirm}
          disabled={saving || !croppedAreaPixels}
        >
          {saving ? "Processando…" : "Usar recorte"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
